import { TrackedOverlayContext } from '@objectiv/tracker-react';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { InputLanguageSelect } from '../../InputLanguageSelect';
import { ProfileInfo } from '../../ProfileInfo/ProfileInfo';
import { MenuItem } from '../MenuItem';

import styles from './MenuDrawer.module.css';

import type { MenuItemProps } from '../MenuItem';
import type { ComponentProps } from 'react';
import type React from 'react';

interface MenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  menuItems: MenuItemProps[];
}

const MenuDrawer: React.FC<MenuDrawerProps> = ({ isOpen, onClose, menuItems }) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [translateY, setTranslateY] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);

  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  const firstMenuItemRef = useRef<HTMLButtonElement | null>(null);

  // TODO: Focus management -> Button component currently doesn't support ref forwarding
  useEffect(() => {
    if (isOpen) {
      const moreButton = document.querySelector('button[aria-label="More"]') as HTMLButtonElement;
      if (moreButton) {
        moreButtonRef.current = moreButton;
      }

      if (firstMenuItemRef.current) {
        setTimeout(() => {
          firstMenuItemRef.current?.focus();
        }, 300);
      }
    } else if (moreButtonRef.current) {
      moreButtonRef.current.focus();
    }
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY === null || !isDragging) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY;

    // Only allow dragging down (positive diff)
    if (diff > 0) {
      setTranslateY(diff);
      // Prevent default to stop background scrolling
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    if (touchStartY === null) return;

    // If dragged more than 100px or 40% of drawer height, close it
    const drawerHeight = drawerRef.current?.clientHeight || 300;
    if (translateY > Math.min(100, drawerHeight * 0.4)) {
      onClose();
    } else {
      // Reset position
      setTranslateY(0);
    }

    setTouchStartY(null);
    setIsDragging(false);
  };

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setTranslateY(0);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleMenuItemClick = (item: MenuItemProps) => {
    if (item.onClick) {
      console.log('item.onClick', item.onClick);
      item.onClick();
    }
    onClose();
  };

  return ReactDOM.createPortal(
    // accessibility: inert is used to prevent screen readers from reading the content behind the drawer
    // when the drawer is open. typescript doesn't have support for inert attribute yet, hence the workaround
    <TrackedOverlayContext<ComponentProps<'div'>>
      className={`${styles.drawerContainer} ${isOpen ? styles.open : ''}`}
      {...{ inert: !isOpen ? '' : undefined }}
      objectiv={{ Component: 'div', id: 'menu-drawer', isVisible: isOpen }}
    >
      <div
        className={styles.overlay}
        ref={overlayRef}
        onClick={handleOverlayClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleOverlayClick(e as unknown as React.MouseEvent);
          }
        }}
        tabIndex={0}
        role='button'
        aria-label='Close menu'
      />
      <div
        id='menu-drawer'
        role='dialog'
        aria-modal='true'
        aria-label='More menu options'
        className={styles.drawer}
        ref={drawerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className={styles.drawerHandle}>
          <div className={styles.handleBar} />
        </div>
        <div className={styles.drawerContent}>
          <div className={styles.menuList}>
            <ProfileInfo />
            <InputLanguageSelect />
            {menuItems.map((item) => (
              <MenuItem key={item.id} {...item} layout='wide' onClick={() => handleMenuItemClick(item)} />
            ))}
          </div>
        </div>
      </div>
    </TrackedOverlayContext>,
    document.body,
  );
};

export { MenuDrawer };
